import { useState, useMemo } from "react";
import { Box, VStack, Text, HStack, Icon } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { TreeView, createTreeCollection } from "components/ui";
import {
  LuChevronRight,
  LuBraces,
  LuType,
  LuHash,
  LuCheckCircle,
} from "react-icons/lu";

interface JsonPathSelectorProps {
  jsonExample: string;
  selectedPath: string | null;
  onPathSelect: (path: string) => void;
}

interface JsonNode {
  id: string;
  name: string;
  path: string;
  type: "object" | "array" | "string" | "number" | "boolean" | "null";
  value?: unknown;
  children?: JsonNode[];
}

const parseJsonToTree = (
  obj: unknown,
  parentPath: string = "",
  parentName: string = "root",
): JsonNode => {
  const currentPath = parentPath;

  if (obj === null) {
    return {
      id: currentPath || "root",
      name: parentName,
      path: currentPath,
      type: "null",
      value: null,
    };
  }

  const objType = Array.isArray(obj) ? "array" : typeof obj;

  if (objType === "object" && !Array.isArray(obj)) {
    const children: JsonNode[] = [];
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const childPath = parentPath ? `${parentPath}.${key}` : key;
      children.push(parseJsonToTree(value, childPath, key));
    }
    return {
      id: currentPath || "root",
      name: parentName,
      path: currentPath,
      type: "object",
      children: children.length > 0 ? children : undefined,
    };
  } else if (objType === "array") {
    const arr = obj as unknown[];
    if (arr.length === 0) {
      return {
        id: currentPath,
        name: parentName,
        path: currentPath,
        type: "array",
        children: undefined,
      };
    }
    // Show first array element as example
    const childPath = `${parentPath}[0]`;
    const firstElement = parseJsonToTree(arr[0], childPath, `${parentName}[0]`);
    return {
      id: currentPath,
      name: parentName,
      path: currentPath,
      type: "array",
      children: [firstElement],
    };
  } else {
    return {
      id: currentPath,
      name: parentName,
      path: currentPath,
      type: objType as "string" | "number" | "boolean",
      value: obj,
    };
  }
};

const getNodeIcon = (type: JsonNode["type"]) => {
  switch (type) {
    case "object":
      return (
        <Icon color="blue.500">
          <LuBraces />
        </Icon>
      );
    case "array":
      return (
        <Icon color="purple.500">
          <LuBraces />
        </Icon>
      );
    case "string":
      return (
        <Icon color="green.500">
          <LuType />
        </Icon>
      );
    case "number":
      return (
        <Icon color="orange.500">
          <LuHash />
        </Icon>
      );
    default:
      return (
        <Icon color="gray.500">
          <LuType />
        </Icon>
      );
  }
};

export const JsonPathSelector: React.FC<JsonPathSelectorProps> = ({
  jsonExample,
  selectedPath,
  onPathSelect,
}) => {
  const { t } = useTranslation();
  const [parseError, setParseError] = useState<string | null>(null);

  const treeData = useMemo(() => {
    if (!jsonExample.trim()) {
      setParseError(null);
      return null;
    }

    try {
      const parsed = JSON.parse(jsonExample);
      setParseError(null);
      const rootNode = parseJsonToTree(parsed);
      return rootNode;
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Invalid JSON");
      return null;
    }
  }, [jsonExample]);

  const collection = useMemo(() => {
    if (!treeData) return null;

    return createTreeCollection<JsonNode>({
      nodeToValue: (node: JsonNode) => node.id,
      nodeToString: (node: JsonNode) => node.name,
      rootNode: treeData,
    });
  }, [treeData]);

  if (parseError) {
    return (
      <Box
        p={4}
        borderWidth="1px"
        borderRadius="md"
        themeAware
        borderColor="red.200"
      >
        <Text color="red.600" fontSize="sm" fontWeight="medium">
          {t("app.servers.authorization.tokenEndpoint.invalidJson")}:{" "}
          {parseError}
        </Text>
      </Box>
    );
  }

  if (!jsonExample.trim()) {
    return (
      <Box p={4} borderWidth="1px" borderRadius="md" themeAware>
        <Text fontSize="sm">
          {t("app.servers.authorization.tokenEndpoint.pasteJsonPrompt")}
        </Text>
      </Box>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <VStack align="stretch" gap={2} themeAware>
      <Text fontSize="sm" fontWeight="medium">
        {t("app.servers.authorization.tokenEndpoint.selectTokenField")}
      </Text>
      <Box
        borderWidth="1px"
        borderRadius="md"
        themeAware
        maxH="300px"
        overflowY="auto"
        p={2}
      >
        <TreeView.Root
          collection={collection}
          defaultExpandedValue={["root"]}
          onSelectionChange={(details) => {
            const selected = details.selectedValue;
            if (selected && selected.length > 0) {
              const selectedNode = collection.visit((node: JsonNode) => {
                if (node.id === selected[0]) return node;
              });
              if (
                selectedNode &&
                selectedNode.path &&
                selectedNode.type !== "object" &&
                selectedNode.type !== "array"
              ) {
                onPathSelect(selectedNode.path);
              }
            }
          }}
          selectionMode="single"
        >
          <TreeView.Tree>
            {/* @ts-ignore - React 19 type compatibility issue with Chakra UI */}
            <TreeView.Node
              render={({ node, nodeState }: any) => {
                const typedNode = node as JsonNode;
                const isSelected = selectedPath === typedNode.path;
                const isLeaf =
                  !typedNode.children || typedNode.children.length === 0;
                const isSelectable =
                  isLeaf &&
                  typedNode.type !== "object" &&
                  typedNode.type !== "array";

                if (nodeState.isBranch) {
                  return (
                    <TreeView.BranchControl>
                      {getNodeIcon(typedNode.type)}
                      <TreeView.BranchText fontWeight="medium">
                        {typedNode.name}
                      </TreeView.BranchText>
                      {typedNode.type === "array" && (
                        <Text fontSize="xs" color="gray.500" ml={1}>
                          []
                        </Text>
                      )}
                    </TreeView.BranchControl>
                  );
                } else {
                  return (
                    <TreeView.Item
                      cursor={isSelectable ? "pointer" : "default"}
                      bg={
                        isSelected
                          ? { base: "blue.50", _dark: "blue.900" }
                          : "transparent"
                      }
                      _hover={
                        isSelectable
                          ? { bg: { base: "gray.50", _dark: "whiteAlpha.100" } }
                          : undefined
                      }
                      onClick={
                        isSelectable
                          ? () => onPathSelect(typedNode.path)
                          : undefined
                      }
                    >
                      <HStack gap={2}>
                        {getNodeIcon(typedNode.type)}
                        <TreeView.ItemText>{typedNode.name}</TreeView.ItemText>
                        {typedNode.value !== undefined && (
                          <Text
                            fontSize="xs"
                            color={{ base: "gray.500", _dark: "gray.400" }}
                            maxW="200px"
                            css={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            = {String(typedNode.value)}
                          </Text>
                        )}
                        {isSelected && (
                          <Icon color="blue.500" ml="auto">
                            <LuCheckCircle />
                          </Icon>
                        )}
                      </HStack>
                    </TreeView.Item>
                  );
                }
              }}
            />
          </TreeView.Tree>
        </TreeView.Root>
      </Box>
      {selectedPath && (
        <Box p={2} borderRadius="md" borderWidth="1px" themeAware>
          <Text fontSize="sm">
            <strong>
              {t("app.servers.authorization.tokenEndpoint.selectedPath")}:
            </strong>{" "}
            {selectedPath}
          </Text>
        </Box>
      )}
    </VStack>
  );
};
